import { intentKey } from '../../core/http/intent';
import { invitationToken } from '../../core/http/invitation-link';
import { selectClub } from '../../core/state/club-session';
import { privateLeaguesApi, privateError } from '../../core/http/private-leagues.api';
import {
  Component,
  ElementRef,
  inject,
  signal,
  computed,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GameService } from '../../core/state/game.service';
import {
  UserCompetitionsState,
  CompetitionStructure,
  UserParticipation,
  DivisionStructure,
  PrivateLeagueBackendSummary,
} from '../../core/models/types';
import { competitionApi } from '../../core/http/competition.api';
import { CompetitionService } from '../../core/http/competition.service';
import { clubsApi } from '../../core/http/clubs.api';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { SettingsModalComponent } from '../../shared/ui/settings-modal/settings-modal.component';
import { Language } from '../../core/i18n/translations';

@Component({
  selector: 'app-league-selection',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponent,
    SettingsModalComponent,
  ],
  templateUrl: './league-selection.component.html',
  styleUrl: './league-selection.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class LeagueSelectionComponent implements OnInit {
  readonly game = inject(GameService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly elementRef = inject(ElementRef);
  private readonly competitionService = inject(CompetitionService);

  // UI state
  readonly isUserMenuOpen = signal<boolean>(false);
  readonly isSettingsModalOpen = signal<boolean>(false);

  // Backend loading and error states
  readonly isLoading = signal<boolean>(true);
  readonly isSubmittingClub = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);

  // Real backend data signals
  readonly competitionsState = signal<UserCompetitionsState | null>(null);
  readonly structure = signal<CompetitionStructure | null>(null);

  // Form for joining global competition (NOT_JOINED)
  newClubName = '';

  // Modals for private leagues
  showCreateModal = false;
  showJoinModal = false;

  newLeagueName = '';
  newPrivateClubName = '';

  joinCode = '';
  joinClubName = '';

  ngOnInit(): void {
    const fragment = this.route.snapshot.fragment;
    if (fragment?.startsWith('invitation=')) {
      this.joinCode = invitationToken(`/leagues#${fragment}`);
      this.showJoinModal = true;
    }
    void this.loadData().then(() => {
      if (fragment?.startsWith('invitation=') && !this.joinCode) this.errorMessage.set('El enlace de invitación no es válido.');
    });
  }

  /**
   * Loads user competition state from GET /api/me/competitions
   * and complements with GET /api/competitions/{id}/structure
   */
  async loadData(): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      const state = await this.competitionService.getMyCompetitions(true);
      this.competitionsState.set(state);


      const compId = state.global?.competitionId;
      if (compId) {
        try {
          const struct = await this.competitionService.getCompetitionStructure(compId);
          this.structure.set(struct);
        } catch {
          // If structure fails or is unavailable, state is still rendered
        }
      }
    } catch (err: any) {
      this.errorMessage.set(
        err?.message || 'Error al conectar con el servidor de competiciones.'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  // --- Computed Domain Properties ---

  /**
   * Determines if user is JOINED or NOT_JOINED
   */
  readonly isJoined = computed<boolean>(() => {
    const state = this.competitionsState();
    if (!state) return false;
    const status = (
      state.global?.status ||
      state.status ||
      state.state ||
      ''
    ).toUpperCase();
    if (status === 'JOINED') return true;
    if (status === 'NOT_JOINED') return false;

    // Fallback: check if active club or participation is present
    const part = this.activeParticipation();
    return Boolean(
      part?.club || part?.clubName || state.club || state.global?.club
    );
  });

  /**
   * Determines if user can join the global competition
   */
  readonly canJoin = computed<boolean>(() => {
    const state = this.competitionsState();
    if (!state) return false;
    return state.global?.canJoin ?? state.canJoin ?? true;
  });

  /**
   * Resolves the active user participation among potentially multiple cycles.
   * Does NOT assume the first element is the active cycle.
   */
  readonly activeParticipation = computed<UserParticipation | undefined>(() => {
    const state = this.competitionsState();
    if (!state) return undefined;
    const parts = state.participations || state.global?.participations || [];
    if (!parts || parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];

    // 1. Explicit active status
    const activeExplicit = parts.find(
      (p) =>
        p.status?.toUpperCase() === 'ACTIVE' ||
        (p as any).active === true ||
        (p as any).isCurrent === true
    );
    if (activeExplicit) return activeExplicit;

    // 2. Matching global cycle
    const targetCycle =
      state.global?.cycleNumber ?? (state.global as any)?.currentCycle;
    if (targetCycle !== undefined && targetCycle !== null) {
      const matchingCycle = parts.find((p) => p.cycleNumber === targetCycle);
      if (matchingCycle) return matchingCycle;
    }

    // 3. Matching global division
    if (state.global?.divisionId) {
      const matchingDiv = parts.find((p) => p.divisionId === state.global?.divisionId);
      if (matchingDiv) return matchingDiv;
    }

    // 4. Highest cycleNumber (latest cycle)
    const sorted = [...parts].sort(
      (a, b) => (b.cycleNumber || 0) - (a.cycleNumber || 0)
    );
    return sorted[0];
  });

  /**
   * Matches user's participation with division in structure:
   * participation.divisionId === division.id (never by array index)
   */
  readonly matchingDivision = computed<DivisionStructure | undefined>(() => {
    const part = this.activeParticipation();
    const struct = this.structure();
    if (!part || !struct?.divisions?.length) return undefined;
    if (part.divisionId) {
      const found = struct.divisions.find((d) => d.id === part.divisionId);
      if (found) return found;
    }
    if (part.divisionLevel !== undefined && part.divisionLevel !== null) {
      const foundByLevel = struct.divisions.find((d) => d.level === part.divisionLevel);
      if (foundByLevel) return foundByLevel;
    }
    return undefined;
  });

  /**
   * Real user club name from backend
   */
  readonly activeClubName = computed<string>(() => {
    const part = this.activeParticipation();
    const state = this.competitionsState();
    if (part?.clubName) return part.clubName;
    if (part?.club) {
      return typeof part.club === 'object' ? part.club.name || '' : part.club;
    }
    if (state?.club) {
      return typeof state.club === 'object' ? state.club.name || '' : state.club;
    }
    if (state?.global?.club) {
      return typeof state.global.club === 'object'
        ? state.global.club.name || ''
        : state.global.club;
    }
    return '';
  });

  /**
   * Real user club id from /competitions (participations or club object)
   */
  readonly activeClubId = computed<string | null>(() => {
    const part = this.activeParticipation();
    const state = this.competitionsState();
    if (part?.clubId) return part.clubId;
    if (part?.club && typeof part.club === 'object' && part.club.id) {
      return part.club.id;
    }
    if (state?.club && typeof state.club === 'object' && state.club.id) {
      return state.club.id;
    }
    if (state?.global?.club && typeof state.global.club === 'object' && state.global.club.id) {
      return state.global.club.id;
    }
    return null;
  });

  /**
   * Season name from structure or state
   */
  readonly seasonName = computed<string>(() => {
    const struct = this.structure();
    const state = this.competitionsState();
    const part = this.activeParticipation();
    return (
      struct?.seasonName ||
      state?.global?.seasonName ||
      part?.seasonName ||
      'Global 2026/27'
    );
  });

  /**
   * Division name from matched division or participation
   */
  readonly divisionName = computed<string>(() => {
    const div = this.matchingDivision();
    const part = this.activeParticipation();
    const state = this.competitionsState();
    return div?.name || part?.divisionName || state?.global?.divisionName || '';
  });

  /**
   * Division format (e.g. MINI_SEASON)
   */
  readonly format = computed<string>(() => {
    const div = this.matchingDivision();
    return div?.format || '';
  });

  /**
   * Cycle text: "Ciclo X de N"
   */
  readonly cycleText = computed<string>(() => {
    const part = this.activeParticipation();
    const div = this.matchingDivision();
    const state = this.competitionsState();
    const cycle = part?.cycleNumber ?? state?.global?.cycleNumber;
    const maxCycles = div?.cyclesPerRealSeason;
    if (cycle !== undefined && cycle !== null) {
      if (maxCycles) {
        return `Ciclo ${cycle} de ${maxCycles}`;
      }
      return `Ciclo ${cycle}`;
    }
    return '';
  });

  /**
   * Team count / capacity text: e.g. "6 / 6 equipos"
   */
  readonly teamCountText = computed<string>(() => {
    const part = this.activeParticipation();
    const div = this.matchingDivision();
    const state = this.competitionsState();
    const count =
      part?.teamCount ?? state?.global?.teamCount ?? div?.clubsPerGroup ?? null;
    const capacity =
      part?.teamCapacity ?? state?.global?.teamCapacity ?? div?.clubsPerGroup ?? null;
    if (count !== null && capacity !== null && capacity > 0) {
      return `${count} / ${capacity} equipos`;
    }
    if (count !== null && count > 0) {
      return `${count} equipos`;
    }
    return '';
  });

  /**
   * Matchday validations:
   * ONLY display when currentMatchday and totalMatchdays have valid numbers (> 0).
   * Do NOT display when currentMatchday is null or totalMatchdays is 0.
   */
  readonly hasValidMatchdays = computed<boolean>(() => {
    const part = this.activeParticipation();
    const state = this.competitionsState();
    const current = part?.currentMatchday ?? state?.global?.currentMatchday;
    const total = part?.totalMatchdays ?? state?.global?.totalMatchdays;
    return (
      current !== null &&
      current !== undefined &&
      current > 0 &&
      total !== null &&
      total !== undefined &&
      total > 0
    );
  });

  readonly currentMatchday = computed<number | null>(() => {
    const part = this.activeParticipation();
    const state = this.competitionsState();
    return part?.currentMatchday ?? state?.global?.currentMatchday ?? null;
  });

  readonly totalMatchdays = computed<number | null>(() => {
    const part = this.activeParticipation();
    const state = this.competitionsState();
    return part?.totalMatchdays ?? state?.global?.totalMatchdays ?? null;
  });

  /**
   * Backend-only private leagues (no mock data!)
   */
  readonly privateLeagues = computed<PrivateLeagueBackendSummary[]>(() => {
    const state = this.competitionsState();
    return state?.privateLeagues || [];
  });

  readonly privateLeaguesEnabled = computed<boolean>(() => {
    const state = this.competitionsState();
    return Boolean(state?.privateLeaguesEnabled);
  });

  // --- Actions ---

  /**
   * POST /api/clubs
   * Creates club and joins the global competition.
   * Avoids double submission with isSubmittingClub flag.
   */
  async handleCreateClub(): Promise<void> {
    const name = this.newClubName.trim();
    if (!name || this.isSubmittingClub()) return;

    this.isSubmittingClub.set(true);
    this.errorMessage.set(null);

    try {
      await this.competitionService.createClub(name);
      this.newClubName = '';
      // Refresh real state from GET /api/me/competitions
      await this.loadData();
      this.game.showNotification(
        '¡Club creado y registrado en la competición con éxito!',
        'success'
      );
    } catch (err: any) {
      this.errorMessage.set(err?.message || 'Error al crear el club.');
      this.game.showNotification(
        this.errorMessage() || 'Error al crear el club',
        'error'
      );
    } finally {
      this.isSubmittingClub.set(false);
    }
  }

  /**
   * Enters user's club in the global competition, preserving navigation to /dashboard.
   * Sets club.id in state for subsequent pages like /squad.
   */
  async handleEnterClub(): Promise<void> {
    const club = this.activeClubId();
    if (!club) return;
    selectClub(club);
    window.location.assign('/competition');
  }

  /**
   * Enters a private league details/context page
   */
  async handleSelectPrivate(league: PrivateLeagueBackendSummary): Promise<void> {
    if (league.capabilities && !league.capabilities.viewContext) return;
    await this.router.navigate(['/private-leagues', league.privateLeagueId, 'clubs', league.club.id]);
  }

  /**
   * Directly enters user's club in an active private league.
   * Sets club session and routes to the competition dashboard.
   */
  handleEnterPrivateClub(league: PrivateLeagueBackendSummary): void {
    const clubId = league.club?.id;
    if (!clubId) return;
    selectClub(clubId);
    window.location.assign('/competition');
  }

  async handleCreateSubmit(): Promise<void> {
    if (!this.privateLeaguesEnabled() || this.isSubmittingClub()) return;
    const name=this.newLeagueName.trim(), club=this.newPrivateClubName.trim();
    if (!name || !club) return;
    this.isSubmittingClub.set(true); this.errorMessage.set(null);
    try {
      const intent=await intentKey('create-league',[name,club]);
      const result=await privateLeaguesApi.create(name,club,intent.key);
      intent.complete(); this.showCreateModal=false;
      await this.router.navigate(['/private-leagues',result.leagueId,'clubs',result.clubId]);
    } catch(error) { this.errorMessage.set(privateError(error)); }
    finally { this.isSubmittingClub.set(false); }
  }
  async handleJoinSubmit(): Promise<void> {
    if (!this.privateLeaguesEnabled() || this.isSubmittingClub()) return;
    const invitation=invitationToken(this.joinCode),club=this.joinClubName.trim();
    if (!invitation) { this.errorMessage.set('El enlace o código de invitación no es válido.'); return; }
    if (!invitation || !club) return;
    this.isSubmittingClub.set(true); this.errorMessage.set(null);
    try {
      const intent=await intentKey('join-league',[invitation,club]);
      const result=await privateLeaguesApi.join(invitation,club,intent.key);
      intent.complete(); this.joinCode=''; this.showJoinModal=false;
      await this.router.navigate(['/private-leagues',result.leagueId,'clubs',result.clubId]);
    } catch(error) { this.errorMessage.set(privateError(error)); }
    finally { this.isSubmittingClub.set(false); }
  }

  // --- Header, Settings & Modals ---

  toggleUserMenu(): void {
    this.isUserMenuOpen.update((v) => !v);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  async handleLogout(): Promise<void> {
    this.closeUserMenu();
    await this.game.logout();
  }

  selectLanguage(lang: Language): void {
    this.game.i18n.setLanguage(lang);
  }

  openSettingsModal(): void {
    this.isSettingsModalOpen.set(true);
    this.closeUserMenu();
  }

  closeSettingsModal(): void {
    this.isSettingsModalOpen.set(false);
  }

  paySubscription(): void {
    this.game.payMonthlySubscription();
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.isUserMenuOpen()) {
      const dropdown = this.elementRef.nativeElement.querySelector(
        '#league-user-menu-dropdown'
      );
      const trigger = this.elementRef.nativeElement.querySelector(
        '#btn-league-settings'
      );
      if (
        dropdown &&
        !dropdown.contains(target) &&
        trigger &&
        !trigger.contains(target)
      ) {
        this.closeUserMenu();
      }
    }
  }

  onEscape(): void {
    this.closeUserMenu();
    this.closeSettingsModal();
    this.showCreateModal = false;
    this.showJoinModal = false;
  }
}

